import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { clientService } from "../../../../../api";
import { Button, ModalVariables, Table, TopProgress } from "../../../../../components";
import { useUi } from "../../../../../context/ui.context";
import { useAuth } from "../../../../../context/user.context";
import { icons } from "../../../../../icons";
import { permissionSurfaces, usePermission, usePermissionDecision } from '../../../../../permissions';
import {
  PageContainer
} from '../styles'

const Users:FC = () => {
  const [t] = useTranslation()
  const navigate = useNavigate()
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [modalType, setModalType] = useState<any>('')
  const [users, setUsers] = useState<any[]>([])
  const [selectedUserId, setSelectedUserId] = useState<any>(false)
  const [trigger, setTrigger] = useState(false)
  const [userToEdit, setUserToEdit] = useState()
  const {viewAlert} = useUi()
  const {id} = useParams()
  const {
    setAvailableNotifications,
    setAvailableRoles,
    setClientToInvite,
    clientForInvite
  } = useAuth()
  const [paginationData, setPaginationData] = useState<any>()
  const { mergeEntityPermissions } = usePermission()
  const canInviteUsers = usePermissionDecision(permissionSurfaces.pageActions.clientInvite).allowed
  const canEditUserRoles = usePermissionDecision(permissionSurfaces.rowActions.clientUserEditRoles, {
    extraGuards: { invitationAccepted: true },
  }).allowed
  const updateUsers = () => {
    if(id){
      // clientService.getInvites()
      // .then((res:any) => {
      //   // setPaginationData(res.data.pagination)
      // })
      // .catch((e:any) => {
      //   if(e?.response?.data?.detail){
      //     viewAlert({
      //       severity:'error',
      //       message:e?.response?.data?.detail
      //     })
      //   }
      // })
      clientService.getClientUsers(id)
      .then((res:any) => {
        mergeEntityPermissions('clients', res.data, `Merged client ${id} users permissions`)
        const mapUserToRow = (user:any, invitationPending:boolean) => ({
          ...user,
          email: user.email,
          _invitation: invitationPending ? t("NO") : t("YES"),
          roles: Array.isArray(user.roles) ? user.roles.map((role:any) => role.display_name) : [],
          id: user.id,
          invitation: invitationPending,
          _roles: Array.isArray(user.roles) ? user.roles : [],
          invitation_id: user.invitation_id,
          allowed_actions: user.allowed_actions,
        })

        const acceptedUsers = (res?.data?.users || []).map((user:any) => mapUserToRow(user, false))
        const invitedUsers = (res?.data?.invited_users || []).map((user:any) => mapUserToRow(user, true))

        setUsers([...acceptedUsers, ...invitedUsers])
      })
      .catch((e:any) => {
        if(e?.response?.data?.detail){
          viewAlert({
            severity:'error',
            message:e?.response?.data?.detail
          })
        }
      })
      .finally(() => {
        setPageLoading(false)
      })
    }
  }
  const closeModal = () => {
    if(modalType === 'universal'){
      _dissociateUser(userToEdit)
      setModalType(null)
    }
    setViewModal(false)
    setActiveAnimation(false)
    updateUsers()
    // navigate("/instances")
  }
  const closeModalCancel = () => {
    setViewModal(false)
    setActiveAnimation(false)
  }
  const openModal = (modal:any) => {
    setModalType(modal)
    setViewModal(true)
    setActiveAnimation(true)
  }

  const loadAvailableRoles = async () => {
    if (!id) {
      return false
    }

    try {
      const roles:any = await clientService.getAvailableRoles(id)
      setAvailableRoles(roles.data.roles || [])

      return true
    } catch (e:any) {
      if (e?.response?.data?.detail) {
        viewAlert({
          severity:'error',
          message:e.response.data.detail,
        })
      }

      return false
    }
  }

  const loadInviteData = async () => {
    if (!canInviteUsers || !id) {
      return false
    }

    const rolesLoaded = await loadAvailableRoles()

    if (!rolesLoaded) {
      return false
    }

    try {
      const notifications:any = await clientService.getUserNotificationSettings(id)
      setAvailableNotifications(Object.entries(notifications.data.detail).map((e) => e[1]))
      setTrigger((current) => !current)

      return true
    } catch (e:any) {
      if (e?.response?.data?.detail) {
        viewAlert({
          severity:'error',
          message:e.response.data.detail,
        })
      }

      return false
    }
  }

  const openInviteModal = async () => {
    const loaded = await loadInviteData()

    if (!loaded) {
      return
    }

    openModal('invite-user')
  }

  const openEditRolesModal = async (item:any) => {
    const loaded = await loadAvailableRoles()

    if (!loaded) {
      return
    }

    setSelectedUserId(item)
    openModal('edit-roles')
  }

  useEffect(() => {
    setUsers([])
    setPageLoading(true)
    if (canInviteUsers || canEditUserRoles) {
      void loadAvailableRoles()
    }
  },[canEditUserRoles, canInviteUsers, id])

  useEffect(() => {
    updateUsers()
    const interval = setInterval(() => {
      updateUsers()
    },10000)
    return () => clearInterval(interval)
  },[id])

  const _dissociateUser = (user:any) => {
    if(user?.notInvited) {
      clientService.cancelInvitation(id!, {invitation_id: user.invitation_id})
      .then((res) => {
        viewAlert({
          severity:'info',
          message:res.data?.detail || 'Invitation cancelled'
        })
        setViewModal(false)
        setActiveAnimation(false)
        updateUsers()
      })
      .catch((e) => {
        if(e?.response?.data?.detail){
          viewAlert({
            severity:'error',
            message:e?.response?.data?.detail
          })
        }
      })
    } else {
      const data = {
        id: id,
        relation_id: user.invitation_id
      }
      clientService.dissociateUser(id, data)
      .then((res) => {
        if(res.data.detail === 'User dissociated'){
          viewAlert({
            severity:'info',
            message:res.data.detail
          })
        }
        setViewModal(false)
        setActiveAnimation(false)
        updateUsers()
      })
      .catch((e) => {
        if(e?.response?.data?.detail){
          viewAlert({
            severity:'error',
            message:e?.response?.data?.detail
          })
        }
      })
    }
  }
  const items = [
    {
      title:t("EDIT_ROLES"),
      id:'2',
      permissionSurface: permissionSurfaces.rowActions.clientUserEditRoles,
      onClick:openEditRolesModal,
      checkField:'invitation',
      icon:icons.Clients,
      divider:false
    },
    {
      title:t("EDIT_NOTIFICATION_SETTINGS"),
      id:'3',
      permissionSurface: permissionSurfaces.rowActions.clientUserEditNotifications,
      onClick:(item:any) => {
        setSelectedUserId(item)
        openModal('edit-notifications')
      },
      checkField:'invitation',
      icon:icons.Notification,
      divider:false
    },
    {
      title:t("DISSOCIATE_USER"),
      id:'4',
      permissionSurface: permissionSurfaces.rowActions.clientUserDissociate,
      onClick:(item:any) => {
        setUserToEdit(item)
        openModal('universal')
      },
      icon:icons.MinusDefault,
      divider:false
    },
  ]
  const limitedItems = [
    {
      title:t("DISSOCIATE_USER"),
      id:'6',
      icon:icons.MinusDefault,
      permissionSurface: permissionSurfaces.rowActions.clientInvitationCancel,
      onClick:(item:any) => {
        setUserToEdit({...item, notInvited:true});
        openModal('universal')
      },
      divider:false
    },
  ]
  const columns = [
    {
      title:t("EMAIL"),
      key:"email"
    },
    {
      title:t("ACCEPTED_INVITE"),
      key:"_invitation"
    },
    {
      title:t("ROLES"),
      key:"roles",
      withBackground:true,
    },
    {
      title:"",
      key:"props"
    }
  ]
  
  return(
    <PageContainer style={{display:'block'}}>
      <TopProgress loading={pageLoading}/>
      {pageLoading ? null :
        <>
          {clientForInvite && canInviteUsers ?
            <Button
              variant="primary"
              size="display"
              customStyles={{marginBottom:"10px"}}
              onClick={openInviteModal}
              title={t("INVITE_USER")}
            />
          : null}
          <Table
            columns={columns}
            rows={users}
            settings={items}
            paginationData={paginationData}
            limitedItems={limitedItems}
            // updateData={updateList}
            hideOther
            trigger={trigger}
          />
          <ModalVariables
            id={selectedUserId}
            modalType={modalType}
            closeModalCancel={closeModalCancel}
            viewModal={viewModal}
            activeAnimation={activeAnimation}
            closeModal={closeModal}
            alertTitle={t("DISSOCIATE_USER")}
            alertSubTitle={t("DISSOCIATE_USER_ALERT")}
          />
        </>
      }
    </PageContainer>
  )
}

export default Users
